import axios, { AxiosInstance, HttpStatusCode } from "axios";
import { storeToRefs } from "pinia";

export default class ApiVerified {
  public axiosInstance: AxiosInstance;

  constructor(apiBaseUrl: string, overrideAccessToken?: string) {
    this.axiosInstance = axios.create({
      baseURL: `${apiBaseUrl}/`,
      paramsSerializer: (params) => {
        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
          if (Array.isArray(value)) {
            value.forEach((v) => searchParams.append(key, String(v)));
          } else if (value !== null && value !== undefined) {
            searchParams.append(key, String(value));
          }
        }
        return searchParams.toString();
      },
    });

    this.axiosInstance.interceptors.request.use(
      async (config) => {
        const { useSessionStore } = await import("@/stores/core/sessionStore");
        const sessionStore = useSessionStore();
        const { UserInfo } = storeToRefs(sessionStore);
        const accessToken = overrideAccessToken || UserInfo.value.AccessToken || "";
        config.headers["Authorization"] = `Bearer ${accessToken}`;
        return config;
      },
      (error) => Promise.reject(error),
    );
    this.axiosInstance.interceptors.response.use(
      (response) => {
        return response;
      },
      async (error) => {
        const originalRequest = error.config;
        const isExpiredToken =
          error.response?.status === HttpStatusCode.Forbidden && !originalRequest?._retry;
        // An instance built around a caller-supplied token (third-party SSO login) is not
        // backed by the session at all — refreshing the session token here would replay the
        // request with credentials it was deliberately not given, and a dead session would
        // log the user out mid-login.
        if (!isExpiredToken || overrideAccessToken) {
          return Promise.reject(error);
        }
        const { useSessionStore } = await import("@/stores/core/sessionStore");
        const sessionStore = useSessionStore();
        originalRequest._retry = true;
        const newAccessToken = await sessionStore.refreshAccessToken();
        // A failed refresh used to return the unchanged stale token, which was then replayed
        // verbatim — a guaranteed second 403 for every call. No token means the session is
        // unrecoverable and the logout is already under way: surface the original error.
        if (!newAccessToken) {
          return Promise.reject(error);
        }
        originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;
        // Retry on this instance, not the bare `axios` default — the default instance carries
        // none of the interceptors above, so a retry through it is unauthenticated bookkeeping.
        return this.axiosInstance(originalRequest);
      },
    );
  }
}
